import { localSlotThreadKey } from "@/lib/targeting/local-slots";
import { SLOT_THREAD_PREFIX, variantIdOfThreadKey } from "@/lib/targeting/slot-thread";
import { hasMessageSlotCode, WEB_MESSAGE_SLOT_CODES } from "@/lib/web-guard-codes";

/** A `sendMessage` outbox payload (the fields a main-chat re-send needs). */
interface SlotSendPayload {
  text: string;
  threadKey: string | null;
  chatId: string | null;
  buttonValue?: string;
  messageSlot?: unknown;
  media?: unknown[];
}

export interface SlotNotFoundFallback {
  /** The slot the BE does not know: it is a local slot from now on. */
  slotId: string;
  /** The thread whose first send the BE refused. */
  failedThreadKey: string;
  /** The same message, re-sent once without `messageSlot` into the local thread. */
  payload: SlotSendPayload;
}

/**
 * A first send that carried `messageSlot` is answered `WEB_MESSAGE_SLOT_NOT_FOUND` when the BE does not
 * know that slot (an unsaved preview draft, a deleted slot). The visitor's text is never lost and never
 * leaves the slot's conversation: it is re-sent ONCE without `messageSlot` into the local thread
 * (`slot-<slotId>` for a `message:` thread, the same regular thread otherwise); the retry carries no
 * `messageSlot`, so it cannot match again. Returns `null` for everything else (`INVALID` /
 * `THREADKEY_MISMATCH` = client bugs that stay permanent drops, other ops, non-slot sends).
 */
export function slotNotFoundFallback(args: {
  op: { op: string; payload?: unknown };
  error: unknown;
}): SlotNotFoundFallback | null {
  if (args.op.op !== "sendMessage") return null;
  const err = (args.error ?? {}) as { graphQLErrors?: readonly unknown[]; networkError?: unknown };
  if (!hasMessageSlotCode(err)) return null;
  if (!JSON.stringify(err).includes(WEB_MESSAGE_SLOT_CODES.notFound)) return null;
  const p = args.op.payload as SlotSendPayload | undefined;
  const slot = p?.messageSlot as { slotId?: string } | undefined;
  if (!slot?.slotId || !p?.threadKey) return null;
  const { messageSlot: _slot, ...rest } = p;
  const threadKey = p.threadKey.startsWith(SLOT_THREAD_PREFIX)
    ? localSlotThreadKey(slot.slotId, variantIdOfThreadKey(p.threadKey))
    : p.threadKey;
  return {
    slotId: slot.slotId,
    failedThreadKey: p.threadKey,
    payload: { ...rest, threadKey, chatId: null },
  };
}

/**
 * An OLDER BE answers `WEB_MESSAGE_SLOT_THREADKEY_MISMATCH` to the first send of a REGULAR thread that
 * carried `messageSlot`. The message must not be lost: re-send the same payload to the same thread
 * without `messageSlot` (once: the retry carries none, so it cannot match again). A `message:` thread
 * keeps its permanent drop. Returns `null` for everything else.
 */
export function regularThreadMismatchRetry(args: {
  op: { op: string; payload?: unknown };
  error: unknown;
}): SlotSendPayload | null {
  if (args.op.op !== "sendMessage") return null;
  const err = (args.error ?? {}) as { graphQLErrors?: readonly unknown[]; networkError?: unknown };
  if (!hasMessageSlotCode(err)) return null;
  if (!JSON.stringify(err).includes(WEB_MESSAGE_SLOT_CODES.threadKeyMismatch)) return null;
  const p = args.op.payload as SlotSendPayload | undefined;
  if (!p?.messageSlot || !p.threadKey || p.threadKey.startsWith("message:")) return null;
  const { messageSlot: _slot, ...rest } = p;
  return rest;
}
